import React, { useState, useEffect, useRef } from "react";
import "./ChatbotComponent.scss";
import { message } from "antd";
import { SendOutlined } from "@ant-design/icons";
import api from "../lib/api";
import { sanitizeHtml } from "../util/sanitizeHtml";

export interface MessageModel {
  message: string;
  sender: string;
  direction: "incoming" | "outgoing";
  position?: string;
}

// markdownToHtml.ts
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function applyInlineFormatting(text: string): string {
  // Images ![alt](url)
  text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');

  // Links [text](url)
  text = text.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noreferrer">$1</a>',
  );

  // Bold+italic ***text*** or ___text___
  text = text.replace(/(\*\*\*|___)(.+?)\1/g, "<strong><em>$2</em></strong>");

  // Bold **text** or __text__
  text = text.replace(/(\*\*|__)(.+?)\1/g, "<strong>$2</strong>");

  // Italic *text* or _text_
  text = text.replace(/(\*|_)([^*_]+?)\1/g, "<em>$2</em>");

  // Strikethrough ~~text~~
  text = text.replace(/~~(.+?)~~/g, "<del>$1</del>");

  // Inline code `code`
  text = text.replace(/`([^`]+)`/g, (match, code) => {
    return `<code>${escapeHtml(code)}</code>`;
  });

  return text;
}

export function markdownToHtml(markdown: string): string {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");

  let html = "";
  let inUl = false;
  let inOl = false;
  let inBlockquote = false;
  let inCodeBlock = false;
  let codeBlockLang = "";
  let codeBlockLines: string[] = [];

  const closeLists = () => {
    if (inUl) {
      html += "</ul>";
      inUl = false;
    }
    if (inOl) {
      html += "</ol>";
      inOl = false;
    }
  };

  const closeBlockquote = () => {
    if (inBlockquote) {
      html += "</blockquote>";
      inBlockquote = false;
    }
  };

  for (let rawLine of lines) {
    const line = rawLine.replace(/\s+$/g, ""); // trim end

    // Code block start/end ```lang?
    const codeFenceMatch = line.match(/^```(\w+)?\s*$/);
    if (codeFenceMatch) {
      if (!inCodeBlock) {
        // starting a code block
        inCodeBlock = true;
        codeBlockLang = codeFenceMatch[1] || "";
        codeBlockLines = [];
        closeLists();
        closeBlockquote();
      } else {
        // ending a code block
        const codeContent = codeBlockLines.join("\n");
        html += `<pre><code${
          codeBlockLang ? ` class="language-${codeBlockLang}"` : ""
        }>${escapeHtml(codeContent)}</code></pre>`;
        inCodeBlock = false;
        codeBlockLang = "";
        codeBlockLines = [];
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockLines.push(rawLine); // preserve spacing
      continue;
    }

    // Empty line => close lists/blockquote, add paragraph break
    if (line.trim() === "") {
      closeLists();
      closeBlockquote();
      html += "";
      continue;
    }

    // Blockquote
    if (/^>\s+/.test(line)) {
      if (!inBlockquote) {
        closeLists();
        html += "<blockquote>";
        inBlockquote = true;
      }
      const content = line.replace(/^>\s+/, "");
      html += applyInlineFormatting(escapeHtml(content)) + "<br />";
      continue;
    } else {
      closeBlockquote();
    }

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      closeLists();
      const level = headingMatch[1].length;
      const content = headingMatch[2];
      html += `<h${level}>${applyInlineFormatting(
        escapeHtml(content),
      )}</h${level}>`;
      continue;
    }

    // Horizontal rule
    if (/^(\*\s*\*\s*\*|-{3,}|_{3,})$/.test(line.trim())) {
      closeLists();
      html += "<hr />";
      continue;
    }

    // Unordered list item
    const ulMatch = line.match(/^(\*|-|\+)\s+(.*)$/);
    if (ulMatch) {
      if (!inUl) {
        closeBlockquote();
        if (inOl) {
          html += "</ol>";
          inOl = false;
        }
        html += "<ul>";
        inUl = true;
      }
      const content = ulMatch[2];
      html += `<li>${applyInlineFormatting(escapeHtml(content))}</li>`;
      continue;
    }

    // Ordered list item
    const olMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (olMatch) {
      if (!inOl) {
        closeBlockquote();
        if (inUl) {
          html += "</ul>";
          inUl = false;
        }
        html += "<ol>";
        inOl = true;
      }
      const content = olMatch[2];
      html += `<li>${applyInlineFormatting(escapeHtml(content))}</li>`;
      continue;
    }

    // Paragraph (default)
    closeLists();
    closeBlockquote();
    html += `<p>${applyInlineFormatting(escapeHtml(line))}</p>`;
  }

  // Close any open blocks at end
  if (inCodeBlock) {
    const codeContent = codeBlockLines.join("\n");
    html += `<pre><code>${escapeHtml(codeContent)}</code></pre>`;
  }
  closeLists();
  closeBlockquote();

  return html;
}

const Reminder =
  "Remember to only answer question about the analysis of the file. " +
  "Base answers about the file on the analysis data only, but you may also answer general questions about concepts. ";

interface ChatbotProps {
  analysisId: string;
  persistedMessages: MessageModel[];
  onMessagesChange: (messages: MessageModel[]) => void;
}

const ChatbotComponent: React.FC<ChatbotProps> = ({
  analysisId,
  persistedMessages,
  onMessagesChange,
}) => {
  const defaultMessages: MessageModel[] = [
    {
      message: "Hello, I am a Chat Bot for Varist Community edition",
      sender: "ChatGPT",
      direction: "incoming",
    },
  ];

  const [messages, setMessages] = useState<MessageModel[]>(
    persistedMessages.length ? persistedMessages : defaultMessages,
  );
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typing]);

  useEffect(() => {
    if (persistedMessages.length) {
      setMessages(persistedMessages);
    } else {
      setMessages(defaultMessages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedMessages]);

  const updateMessages = (newMessages: MessageModel[]) => {
    setMessages(newMessages);
    onMessagesChange(newMessages);
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: MessageModel = {
      message: Reminder + input.trim(),
      sender: "user",
      direction: "outgoing",
    };

    const newMessages = [...messages, userMessage];
    updateMessages(newMessages);
    setInput("");
    setTyping(true);

    try {
      const messages = newMessages.map((m) => ({
        role:
          m.sender === "ChatGPT" ? ("assistant" as const) : ("user" as const),
        content: m.message,
      }));

      const data = await api.chat({ messages, analysisId });

      const botMessage: MessageModel = {
        message: data.reply,
        sender: "ChatGPT",
        direction: "incoming",
      };

      updateMessages([...newMessages, botMessage]);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 401) {
        const errorMessage: MessageModel = {
          message: "Please sign in to use the chatbot.",
          sender: "ChatGPT",
          direction: "incoming",
        };
        updateMessages([...newMessages, errorMessage]);
      } else {
        message.error(e.message || "Something went wrong");
      }
    } finally {
      setTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chatbot-wrapper">
      <div className="chat-messages">
        {messages.map((msg, i) => {
          let content = msg.message || "";
          if (content.startsWith(Reminder)) {
            content = content.substring(Reminder.length).trim();
          }

          const html = markdownToHtml(content);
          const isBot = msg.direction === "incoming";

          return (
            <div key={i} className={`message ${isBot ? "bot" : "user"}`}>
              <div className="message-content">
                <div
                  className="message-text"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
                />
              </div>
            </div>
          );
        })}
        {typing && (
          <div className="message bot">
            <div className="message-content">
              <div className="message-text typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <textarea
          className="chat-input"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={typing}
        />
        <button
          className="send-icon-button"
          onClick={handleSend}
          disabled={!input.trim() || typing}
          title="Send message"
        >
          <SendOutlined />
        </button>
      </div>
    </div>
  );
};

export default ChatbotComponent;
